import { useState, useMemo } from 'react';
import { useReportBuilderStore } from '../../../stores/reportBuilderStore';
import type { AnnotatedColumn } from '../../../stores/reportBuilderStore';
import type { AggregationType, TimeGranularity } from '../../../types/reportBuilder';
import { MagnifyingGlass, X, Plus, Trash, Calculator } from '@phosphor-icons/react';
import { FIELD_COLORS } from '../fieldEncoding';
import CalculatedFieldBuilder from '../CalculatedFieldBuilder';

const GRANULARITIES: { value: TimeGranularity; label: string }[] = [
  { value: 'year', label: 'Y' },
  { value: 'quarter', label: 'Q' },
  { value: 'month', label: 'M' },
  { value: 'week', label: 'W' },
];

export default function ColumnSelectionStep() {
  const {
    selectedModel,
    getDimensionColumns, getMeasureColumns,
    selectedDimensions, selectedMeasures, timeGroupings,
    addDimension, removeDimension, setDimensions, addMeasure, removeMeasure,
    addTimeGrouping, removeTimeGrouping,
    calculatedFields, removeCalculatedField,
  } = useReportBuilderStore();

  const [search, setSearch] = useState('');
  const [showCalcBuilder, setShowCalcBuilder] = useState(false);

  const dimensionCols = getDimensionColumns();
  const measureCols = getMeasureColumns();

  const sortSelectedFirst = (cols: AnnotatedColumn[]) => {
    const selected: AnnotatedColumn[] = [];
    const unselected: AnnotatedColumn[] = [];
    for (const col of cols) {
      const isSelected = col.is_dimension
        ? selectedDimensions.includes(col.refKey)
        : selectedMeasures.some(m => m.column === col.refKey);
      if (isSelected) selected.push(col);
      else unselected.push(col);
    }
    return [...selected, ...unselected];
  };

  const filteredDims = useMemo(() => {
    const cols = search
      ? dimensionCols.filter(c => c.label.toLowerCase().includes(search.toLowerCase()))
      : dimensionCols;
    return sortSelectedFirst(cols);
  }, [dimensionCols, search, selectedDimensions, selectedMeasures]);

  const filteredMeas = useMemo(() => {
    const cols = search
      ? measureCols.filter(c => c.label.toLowerCase().includes(search.toLowerCase()))
      : measureCols;
    return sortSelectedFirst(cols);
  }, [measureCols, search, selectedDimensions, selectedMeasures]);

  const allDimsSelected = filteredDims.length > 0 && filteredDims.every(col => selectedDimensions.includes(col.refKey));
  const someDimsSelected = filteredDims.some(col => selectedDimensions.includes(col.refKey));
  const allMeasSelected = filteredMeas.length > 0 && filteredMeas.every(col => selectedMeasures.some(m => m.column === col.refKey));
  const someMeasSelected = filteredMeas.some(col => selectedMeasures.some(m => m.column === col.refKey));

  const handleSelectAllDimensions = () => {
    if (allDimsSelected) {
      // Deselect all visible dimensions
      const visibleKeys = new Set(filteredDims.map(c => c.refKey));
      setDimensions(selectedDimensions.filter(k => !visibleKeys.has(k)));
    } else {
      // Select all visible dimensions
      const currentSet = new Set(selectedDimensions);
      for (const col of filteredDims) {
        currentSet.add(col.refKey);
      }
      setDimensions(Array.from(currentSet));
    }
  };

  const handleSelectAllMeasures = () => {
    if (allMeasSelected) {
      // Deselect all visible measures
      for (const col of filteredMeas) {
        const existing = selectedMeasures.find(m => m.column === col.refKey);
        if (existing) removeMeasure(existing.alias);
      }
    } else {
      // Select all visible measures
      for (const col of filteredMeas) {
        if (!selectedMeasures.some(m => m.column === col.refKey)) {
          const defaultAgg = (col.aggregation_types?.[0] || 'count') as AggregationType;
          const aliasKey = col.refKey.replace(/\./g, '_');
          addMeasure({ column: col.refKey, aggregation: defaultAgg, alias: `${defaultAgg}_${aliasKey}` });
        }
      }
    }
  };

  if (!selectedModel) return null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-semibold text-gray-900">Select Columns</h2>
        <p className="mt-1 text-sm text-gray-500">
          Pick dimensions (rows) and measures (values) to include in your report.
        </p>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <MagnifyingGlass className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search fields..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-8 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-2 top-2.5">
            <X size={16} className="text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Dimensions Panel */}
        <div>
          <div className="mb-3 flex items-center gap-2">
            <span className={`h-3 w-3 rounded-sm ${FIELD_COLORS.dimension.bg}`} />
            <h3 className="text-sm font-semibold text-gray-700">Dimensions ({filteredDims.length})</h3>
          </div>
          <div className="max-h-[400px] space-y-1 overflow-y-auto rounded-lg border border-gray-200 bg-white p-2">
            {filteredDims.length > 0 && (
              <div className="flex items-center gap-2 rounded px-2 py-1.5 border-b border-gray-100 mb-1">
                <input
                  type="checkbox"
                  checked={allDimsSelected}
                  ref={(el) => { if (el) el.indeterminate = someDimsSelected && !allDimsSelected; }}
                  onChange={handleSelectAllDimensions}
                  className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="flex-1 text-sm font-medium text-gray-600">
                  {allDimsSelected ? 'Deselect All' : 'Select All'}
                </span>
              </div>
            )}
            {filteredDims.map(col => (
              <DimensionItem
                key={col.refKey}
                col={col}
                isSelected={selectedDimensions.includes(col.refKey)}
                onToggle={() => selectedDimensions.includes(col.refKey) ? removeDimension(col.refKey) : addDimension(col.refKey)}
                timeGroupings={timeGroupings}
                onAddTimeGrouping={addTimeGrouping}
                onRemoveTimeGrouping={removeTimeGrouping}
              />
            ))}
            {filteredDims.length === 0 && (
              <p className="py-4 text-center text-xs text-gray-400">No dimensions found</p>
            )}
          </div>
        </div>

        {/* Measures Panel */}
        <div>
          <div className="mb-3 flex items-center gap-2">
            <span className={`h-3 w-3 rounded-sm ${FIELD_COLORS.measure.bg}`} />
            <h3 className="text-sm font-semibold text-gray-700">Measures ({filteredMeas.length})</h3>
          </div>
          <div className="max-h-[400px] space-y-1 overflow-y-auto rounded-lg border border-gray-200 bg-white p-2">
            {filteredMeas.length > 0 && (
              <div className="flex items-center gap-2 rounded px-2 py-1.5 border-b border-gray-100 mb-1">
                <input
                  type="checkbox"
                  checked={allMeasSelected}
                  ref={(el) => { if (el) el.indeterminate = someMeasSelected && !allMeasSelected; }}
                  onChange={handleSelectAllMeasures}
                  className="h-3.5 w-3.5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                <span className="flex-1 text-sm font-medium text-gray-600">
                  {allMeasSelected ? 'Deselect All' : 'Select All'}
                </span>
              </div>
            )}
            {filteredMeas.map(col => (
              <MeasureItem
                key={col.refKey}
                col={col}
                isSelected={selectedMeasures.some(m => m.column === col.refKey)}
                onToggle={() => {
                  if (selectedMeasures.some(m => m.column === col.refKey)) {
                    const existing = selectedMeasures.find(m => m.column === col.refKey);
                    if (existing) removeMeasure(existing.alias);
                  } else {
                    const defaultAgg = (col.aggregation_types?.[0] || 'count') as AggregationType;
                    const aliasKey = col.refKey.replace(/\./g, '_');
                    addMeasure({ column: col.refKey, aggregation: defaultAgg, alias: `${defaultAgg}_${aliasKey}` });
                  }
                }}
              />
            ))}
            {filteredMeas.length === 0 && (
              <p className="py-4 text-center text-xs text-gray-400">No measures found</p>
            )}
          </div>
        </div>
      </div>

      {/* Calculated Fields */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calculator className="h-4 w-4 text-orange-500" />
            <h3 className="text-sm font-semibold text-gray-700">Calculated Fields</h3>
          </div>
          <button
            onClick={() => setShowCalcBuilder(true)}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-blue-600 hover:bg-blue-50"
          >
            <Plus className="h-3 w-3" /> Add
          </button>
        </div>
        {calculatedFields.length > 0 ? (
          <div className="space-y-1 rounded-lg border border-gray-200 bg-white p-2">
            {calculatedFields.map(cf => (
              <div key={cf.key} className="flex items-center justify-between rounded bg-orange-50 px-2 py-1.5">
                <div>
                  <span className="text-sm text-gray-700">{cf.label}</span>
                  <span className="ml-2 text-[10px] font-medium uppercase text-orange-500">
                    {cf.field_type === 'case_bucket' ? 'Bucket' : cf.field_type === 'date_diff' ? 'Date Diff' : 'Ratio'}
                  </span>
                  <span className="ml-1 text-[10px] text-gray-400">
                    ({cf.as_dimension ? 'dimension' : 'measure'})
                  </span>
                </div>
                <button onClick={() => removeCalculatedField(cf.key)} className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500">
                  <Trash className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400 italic">No calculated fields. Add buckets, date diffs, or ratios.</p>
        )}
      </div>

      {showCalcBuilder && <CalculatedFieldBuilder open={showCalcBuilder} onClose={() => setShowCalcBuilder(false)} />}
    </div>
  );
}

function DimensionItem({
  col,
  isSelected,
  onToggle,
  timeGroupings,
  onAddTimeGrouping,
  onRemoveTimeGrouping,
}: {
  col: AnnotatedColumn;
  isSelected: boolean;
  onToggle: () => void;
  timeGroupings: { column: string; granularity: TimeGranularity }[];
  onAddTimeGrouping: (tg: { column: string; granularity: TimeGranularity }) => void;
  onRemoveTimeGrouping: (column: string) => void;
}) {
  const isDateCol = col.data_type === 'date' || col.data_type === 'timestamp';
  const activeTg = timeGroupings.find(tg => tg.column === col.refKey);

  return (
    <div className={`flex items-center gap-2 rounded px-2 py-1.5 ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
      <input
        type="checkbox"
        checked={isSelected}
        onChange={onToggle}
        className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
      />
      <span className="flex-1 truncate text-sm text-gray-700">{col.label}</span>
      {isDateCol && (
        <div className="flex gap-0.5">
          {GRANULARITIES.map(g => {
            const isActive = activeTg?.granularity === g.value;
            return (
              <button
                key={g.value}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isActive) {
                    onRemoveTimeGrouping(col.refKey);
                  } else {
                    onRemoveTimeGrouping(col.refKey);
                    onAddTimeGrouping({ column: col.refKey, granularity: g.value });
                  }
                }}
                className={`rounded px-1 py-0.5 text-[9px] font-semibold leading-none transition-colors ${
                  isActive ? 'bg-teal-600 text-white' : 'bg-gray-200/60 text-gray-400 hover:bg-gray-200 hover:text-gray-600'
                }`}
              >
                {g.label}
              </button>
            );
          })}
        </div>
      )}
      <span className="text-[10px] font-medium uppercase text-gray-400">{col.data_type === 'date' || col.data_type === 'timestamp' ? 'DATE' : col.data_type === 'integer' || col.data_type === 'decimal' ? 'NUM' : 'TEXT'}</span>
    </div>
  );
}

function MeasureItem({
  col,
  isSelected,
  onToggle,
}: {
  col: AnnotatedColumn;
  isSelected: boolean;
  onToggle: () => void;
}) {
  return (
    <div className={`flex items-center gap-2 rounded px-2 py-1.5 ${isSelected ? 'bg-green-50' : 'hover:bg-gray-50'}`}>
      <input
        type="checkbox"
        checked={isSelected}
        onChange={onToggle}
        className="h-3.5 w-3.5 rounded border-gray-300 text-green-600 focus:ring-green-500"
      />
      <span className="flex-1 truncate text-sm text-gray-700">{col.label}</span>
      {col.aggregation_types && col.aggregation_types.length > 0 && (
        <span className="text-[10px] text-gray-400">{col.aggregation_types.join(', ')}</span>
      )}
    </div>
  );
}
